import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { ShellUser, UserMenuEntry } from './types'
import { initials } from './NavDrawer'
import { resolveUserName } from './userName'

export interface UserMenuProps {
  user: ShellUser
  entries: UserMenuEntry[]
}

/** Benutzermenü (Dropdown am Namen im Header): Esc und Klick außerhalb schließen, Fokus kehrt zurück. */
export function UserMenu({ user, entries }: UserMenuProps) {
  const name = resolveUserName(user)
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btn.current?.focus() } }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const close = () => setOpen(false)

  return (
    <div ref={root} className="relative flex">
      <button
        ref={btn}
        type="button"
        className="ui-user"
        onClick={() => setOpen(o => !o)}
        aria-label="Benutzer-Menü öffnen"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="ui-avatar" aria-hidden="true">{initials(name)}</span>
        <span className="ui-user-name">{name}</span>
        {user.dot && <span className="ui-user-dot" role="img" aria-label="Neue Benachrichtigungen" />}
        <ChevronDown className="ui-user-chev h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {open && (
        <div className="ui-menu ui-pop animate-fade-in" role="menu" aria-label="Benutzermenü">
          <div className="ui-menu-user" aria-hidden="true">{name}</div>
          {entries.map((e, i) => {
            if (e.type === 'separator') return <div key={`s${i}`} className="ui-menu-sep" role="separator" />
            if (e.type === 'heading') return <div key={`h${i}`} className="ui-menu-head">{e.label}</div>
            const content = (
              <>
                {e.icon && <span className="ui-menu-ic" aria-hidden="true">{e.icon}</span>}
                <span className="flex-1">{e.label}</span>
                {e.badge !== undefined && e.badge !== 0 && <span className="ui-tag ui-tag--accent">{e.badge}</span>}
              </>
            )
            const cls = e.danger ? 'ui-menu-it ui-menu-it--danger' : 'ui-menu-it'
            if (e.href) {
              return <a key={e.id} role="menuitem" className={cls} href={e.href} target="_blank" rel="noopener" onClick={close}>{content}</a>
            }
            return (
              <button
                key={e.id}
                type="button"
                role="menuitem"
                className={cls}
                onClick={() => { close(); if (e.to) navigate(e.to, e.state ? { state: e.state } : undefined); e.onSelect?.() }}
              >
                {content}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
